import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@/components/core/tabs.tsx";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import { userQueryOptions } from "@/features/dashboard/api/queries/user-queries";
import UserProfileForm from "@/features/dashboard/components/profile-form";
import UserProfilePasswordForm from "@/features/dashboard/components/profile-password-form";
import type { PasswordFormSchema } from "@/features/dashboard/types/user";
import { useAuth } from "@/hooks/use-auth";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { toast } from "sonner";

export const Route = createFileRoute("/_dashboard/user_/$userId/edit")({
	loader: ({ context: { queryClient }, params }) => {
		return queryClient.ensureQueryData(userQueryOptions(params.userId));
	},
	component: UserEdit,
});

function UserEdit() {
	const params = Route.useParams();
	const userQuery = useSuspenseQuery(userQueryOptions(params.userId));
	const user = userQuery.data;
	const { changePassword } = useAuth();
	const handlePasswordSubmit = async (
		data: PasswordFormSchema,
		reset: () => void,
	) => {
		try {
			await changePassword(data);
			toast.success("Profile successfully updated.");
			reset();
		} catch (error) {
			if (error instanceof AxiosError && error.response) {
				toast.error(error.message);
			}
		}
	};

	return (
		<Content>
			<Header />
			<div className="flex flex-col w-full max-w-[1200px] p-6">
				<div className="flex flex-col w-full">
					<Tabs defaultValue="details" orientation="vertical">
						<TabsList>
							<TabsTrigger value="details">Details</TabsTrigger>
							<TabsTrigger value="security">Security</TabsTrigger>
							<TabsTrigger value="roles">Roles</TabsTrigger>
							<TabsTrigger value="sessions">Sessions</TabsTrigger>
							<TabsTrigger value="logs">Logs</TabsTrigger>
						</TabsList>
						<TabsContent value="details">
							<UserProfileForm data={user} />
						</TabsContent>
						<TabsContent value="security">
							<UserProfilePasswordForm onSubmit={handlePasswordSubmit} />
						</TabsContent>
					</Tabs>
				</div>
			</div>
		</Content>
	);
}
